import { signOutOnXimiSessionLost } from '../../utilities/api';
import { ximiUserManager } from '../../utilities/ximi/oidcConfig';
import {
    getXimiUsageReportedDay,
    setXimiUsageReportedDay
} from '../../utilities/ximi/storage/ximiUsageStorage';
import { ximiMobilePostFlat } from './ximiMobileClient';
import { isXimiSessionLostCode } from './ximiStimulinSession';
import { resolveXimiStimulinErrorCode } from './ximiStimulinUnauthorized';

// Ozmo endpoint (PBI 106074): declares that this account used the app today. Ozmo derives the
// billable month (tenant time) and only records agents — the app never computes billing.
// Idempotent within the month, no effect on sessions. Never call api/Token/*: it would bill
// Ximi Mobile.
const STIMULIN_USAGE = 'stimulin/usage';
const HTTP_NO_CONTENT = 204;

/** Local device day, `YYYY-MM-DD`. */
export const toLocalIsoDay = (date: Date): string =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);

async function reportUsage(): Promise<void> {
    const ssoSub = (await ximiUserManager.getUser())?.profile.sub;
    if (!ssoSub) return; // no SSO session: non-Ximi user or not signed in
    const today = toLocalIsoDay(new Date());
    if ((await getXimiUsageReportedDay(ssoSub)) === today) return;

    const { status, data } = await ximiMobilePostFlat<{ code?: string }>(STIMULIN_USAGE);
    if (status === HTTP_NO_CONTENT) {
        await setXimiUsageReportedDay(ssoSub, today);
        return;
    }
    // 401 replayed / 403 lost code → sign-out; 404, 5xx, network → nothing stored, next trigger retries.
    const code = resolveXimiStimulinErrorCode(status, data?.code);
    if (isXimiSessionLostCode(code)) await signOutOnXimiSessionLost(code);
}

let usageReportInFlight: Promise<void> | null = null;

/**
 * Declares today's usage at most once a day per SSO account. Concurrent triggers share one
 * call; never throws (a failure is simply retried at the next trigger).
 */
export function reportXimiStimulinUsageIfDue(): Promise<void> {
    if (!usageReportInFlight) {
        usageReportInFlight = reportUsage()
            .catch(() => undefined)
            .finally(() => {
                usageReportInFlight = null;
            });
    }
    return usageReportInFlight;
}
